import React from 'react';
import {Image, StyleSheet, TouchableOpacity, View} from 'react-native';

import config, {Brand} from '../config';
import responsibleServiceOfAlcoholDisclaimerIcon from '../images/icon-id-25.png';
import {AppRoute} from '../models/AppRoute';
import {InformationContentType} from '../models/navigationTypes';
import * as RootNavigation from '../navigation/RootNavigation';
import theme from '../style/theme';

import Text from './ui-controls/Text';
import {Icon} from './Icon';

type SocialLinks = {
  [key in Brand]?: {
    facebook?: {
      link: string;
      icon: string;
    };
    instagram?: {
      link: string;
      icon: string;
    };
    youtube?: {
      link: string;
      icon: string;
    };
    twitter?: {
      link: string;
      icon: string;
    };
  };
};
const socialLinks: SocialLinks = {
  [Brand.BOTTLEMART]: {
    facebook: {
      link: 'https://www.facebook.com/Bottlemart',
      icon: 'facebook-f',
    },
    instagram: {
      link: 'https://www.instagram.com/bottlemartau',
      icon: 'instagram',
    },
    youtube: {
      link: 'https://www.youtube.com/BottlemartVideos',
      icon: 'youtube',
    },
  },
  [Brand.SIPNSAVE]: {
    facebook: {
      link: 'https://www.facebook.com/pages/Sipn-Save/101508726608993',
      icon: 'facebook-f',
    },
    youtube: {
      link: 'https://www.youtube.com/channel/UCbiGLNEqRzvnghPlOEt4qHg',
      icon: 'youtube',
    },
  },
  [Brand.HARRY_BROWN]: {
    facebook: {
      link: 'https://www.facebook.com/harrybrownliquor',
      icon: 'facebook-f',
    },
    instagram: {
      link: 'https://www.instagram.com/harrybrownliquor',
      icon: 'instagram',
    },
  },
  [Brand.THIRSTY_CAMEL]: {
    facebook: {
      link: 'https://www.facebook.com/ThirstyCamelWA',
      icon: 'facebook-f',
    },
    instagram: {
      link: 'https://www.instagram.com/thirstycamel_wa/',
      icon: 'instagram',
    },
    twitter: {
      link: 'https://twitter.com/thirstycamelwa',
      icon: 'twitter',
    },
  },
};

type LinkProps = {
  children: React.ReactNode;
  target: string;
  params?: any;
};
const Link = ({children, target, params = undefined}: LinkProps) => (
  <TouchableOpacity onPress={() => RootNavigation.navigate(target, params)}>
    <Text style={styles.link}>{children}</Text>
  </TouchableOpacity>
);

const SocialIconLink = ({type}: {type: 'facebook' | 'instagram' | 'youtube' | 'twitter'}) => {
  const targetSocial = socialLinks[config.BASE_TENANT]?.[type];

  return targetSocial ? (
    <a href={targetSocial.link} target="_blank" rel="noreferrer">
      <Icon
        name={targetSocial.icon}
        iconStyle="brand"
        size={16}
        color={theme.colour.general.secondary}
        style={styles.socialIcon}
      />
    </a>
  ) : (
    <></>
  );
};

export default function WebFooter() {
  return (
    <View style={styles.main} testID="web-footer">
      <View style={styles.content} testID="web-footer-content">
        <View style={[styles.contentColumn, styles.leftContentColumn]}>
          <Link target={AppRoute.CONTACT_US}>CONTACT US</Link>
          <Link target={AppRoute.SELF_EXCLUSION}>SELF EXCLUSION</Link>
          <Link target={AppRoute.RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST}>3RD PARTY REQUEST</Link>
        </View>
        <View style={[styles.contentColumn, styles.middleContentColumn]}>
          <Link
            target={AppRoute.INFORMATION_CONTENT}
            params={{
              target: InformationContentType.DELIVERY_AND_PICKUP_FAQS,
            }}>
            DELIVERY & PICK UP FAQS
          </Link>
          <Link
            target={AppRoute.INFORMATION_CONTENT}
            params={{
              target: InformationContentType.RETURNS_AND_EXCHANGES,
            }}>
            RETURNS & EXCHANGES
          </Link>
          <Link
            target={AppRoute.INFORMATION_CONTENT}
            params={{
              target: InformationContentType.TERMS_AND_CONDITIONS,
            }}>
            TERMS & CONDITIONS
          </Link>
          <Link
            target={AppRoute.INFORMATION_CONTENT}
            params={{
              target: InformationContentType.PRIVACY_POLICY,
            }}>
            PRIVACY POLICY
          </Link>
        </View>
        <View style={[styles.contentColumn, styles.rightContentColumn]}>
          <SocialIconLink type="facebook" />
          <SocialIconLink type="instagram" />
          <SocialIconLink type="youtube" />
          <SocialIconLink type="twitter" />
          <View style={styles.followUs}>
            <Text style={styles.followUslink}>FOLLOW{'\n'}US</Text>
          </View>
        </View>
      </View>
      <View style={styles.responsibleServiceOfAlcoholDisclaimerContainer}>
        <Image source={responsibleServiceOfAlcoholDisclaimerIcon} style={{width: 35, height: 35}} />
        <Text style={styles.responsibleServiceOfAlcoholDisclaimerText}>
          {config.APP_NAME} supports the Responsible Service of Alcohol. Liquor Act 2007. It is an
          offence to sell or supply alcohol to, or to obtain alcohol on behalf of, a person aged
          under 18 years.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  main: {
    backgroundColor: theme.colour.background.general.primary,
    marginTop: 8,
    padding: 32,
    paddingHorizontal: 64,
    width: '100%',
  },
  content: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  contentColumn: {
    flex: 0.33,
  },
  leftContentColumn: {},
  middleContentColumn: {},
  rightContentColumn: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  responsibleServiceOfAlcoholDisclaimerContainer: {
    paddingHorizontal: 64,
    paddingTop: 32,
    flexDirection: 'row',
    gap: 8,
  },
  responsibleServiceOfAlcoholDisclaimerText: {
    color: theme.colour.text.drawer.primary,
  },
  link: {
    color: theme.colour.text.drawer.primary,
  },
  followUs: {
    marginLeft: 16,
  },
  followUslink: {
    color: theme.colour.general.secondary,
  },
  socialIcon: {
    marginHorizontal: 4,
  },
});
